import VimeoPlayer from "@u-wave/react-vimeo";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useWindowSize } from "react-use";

import { ImageSlider } from "@/features/common/components/imageCarousel/ImageSlider";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { PostActionButton } from "@/features/post/components/PostActionButton";
import { HoverSubscribeMask } from "@/features/post/components/postCard/components/HoveringMask/HoverSubscribeMask";
import { PostDetailFragment } from "@/generated/generated-hooks";
import { getVideoUrl, isVideoTranscoded } from "@/services/vimeo";

interface PostVideoDisplayProps {
  isOwner: boolean;
  postDetail: PostDetailFragment;
}

export const PostVideoDisplay: FC<PostVideoDisplayProps> = ({ postDetail }) => {
  const { openSnackbar } = useSnackbar();
  const { t } = useTranslation(["post"]);
  const { title, images, videoUri } = postDetail;
  const [postContentWidth, setPostContentWidth] = useState(undefined);
  const { width } = useWindowSize();

  const [videoTranscodeChecked, setVideoTranscodeChecked] = useState<boolean>(
    !videoUri,
  );
  const [videoTranscoded, setVideoTranscoded] = useState<boolean>(false);
  const [videoLink, setVideoLink] = useState<string>();
  const [playStart, setPlayStart] = useState<boolean>(false);

  // check whether post is transcoded
  useEffect(() => {
    const checkVideoTranscodeStatus = async () => {
      const transcoded = await isVideoTranscoded(videoUri);
      setVideoTranscoded(transcoded);
      setVideoTranscodeChecked(true);
    };
    if (!videoTranscodeChecked && videoUri) {
      checkVideoTranscodeStatus();
    }
  }, [videoTranscodeChecked, videoUri]);

  useEffect(() => {
    const updateVideoLink = async () => {
      const url = await getVideoUrl(videoUri);
      setVideoLink(url);
    };
    if (playStart && videoUri && videoTranscoded && videoTranscodeChecked) {
      updateVideoLink();
    }
  }, [videoTranscoded, videoTranscodeChecked, videoUri, playStart]);

  useEffect(() => {
    const element = document.querySelector("#post-content");

    setPostContentWidth(element.clientWidth);
  }, [width]);

  //max-w-3xl
  const maxLength = postContentWidth ?? 768;

  const carouselHeight = useMemo(() => {
    const heights = images.map((image) => {
      if (image.intrinsicSize.width > maxLength) {
        const height = Math.floor(
          (maxLength * image.intrinsicSize.height) / image.intrinsicSize.width,
        );
        return height;
      }

      return image.intrinsicSize.height;
    });
    const maxHeight = Math.max(...heights);
    return maxHeight;
  }, [images, maxLength]);

  if (images?.length === 0) return null;

  const carouselItems = images.map((item) => {
    return { imgSrc: item.url, alt: title, hasAlpha: item.hasAlpha };
  });

  const renderHoverArea = () => {
    if (videoLink) return null;

    if (!videoUri) {
      return (
        <div className="absolute top-0 right-0 left-0 bottom-0 z-20 rounded-lg overflow-hidden">
          <HoverSubscribeMask
            post={postDetail}
            logEventSource="post-carousel"
          />
        </div>
      );
    }

    if (playStart) return null;

    return (
      <div
        className="z-[1] absolute top-0 left-0 w-full h-full flex justify-center items-center touchable"
        onClick={() => setPlayStart(true)}
      >
        <Image
          alt="video-play-icon"
          src="/logo/video-player-default.png"
          layout="fixed"
          width={60}
          height={60}
        />
      </div>
    );
  };

  const renderDisplay = () => {
    if (!playStart || !videoUri) {
      return <ImageSlider items={carouselItems} unoptimized={false} />;
    }
    // check if user auth to view
    // no --> hovermask
    // yes --> display play button
    // click on play button
    // start check if video transcoded

    if (!videoTranscodeChecked) {
      return (
        <div className="font-bold text-lg text-white animate-pulse">
          {t("post:video.loading")}
        </div>
      );
    }

    if (!videoTranscoded) {
      return (
        <div className="font-bold text-lg text-white animate-pulse">
          {t("post:video.transcoding")}
        </div>
      );
    }

    if (!videoLink) {
      return (
        <div className="font-bold text-lg text-white animate-pulse">
          {t("post:video.loading")}
        </div>
      );
    }

    return (
      <VimeoPlayer
        height={carouselHeight > 640 ? carouselHeight : 640}
        video={videoLink}
        autoplay={true}
        width={maxLength - 30}
        showByline={false}
        showTitle={false}
        showPortrait={false}
        onError={(err) => {
          openSnackbar({
            message: err.message,
            severity: "error",
          });
        }}
      />
    );
  };

  return (
    <div className="relative" data-test-id="PostCarousel-container">
      {renderHoverArea()}

      <div className="absolute flex top-2 right-2 z-50">
        <PostActionButton
          postDetail={postDetail}
          portalDomNodeSelector="#PostDialog-fullScreenDialogUtilityButtonContainer"
        />
      </div>

      <div className="relative overflow-hidden md:rounded-lg image-post-detail">
        <div
          className="w-full flex justify-center items-center bg-black"
          style={{
            height: carouselHeight,
            maxHeight: maxLength,
            minHeight: 640,
          }}
        >
          {renderDisplay()}
        </div>
      </div>
    </div>
  );
};
